// Routes are lazily loaded, so each primary screen is its own chunk (checked by check-chunks).
import { useState } from "react"
import { Navigate, RouterProvider, createBrowserRouter, type RouteObject } from "react-router-dom"

import { estimatorEnabled } from "../config"
import { Layout } from "./layout"
import { ProblemBoundary, ProblemView } from "./problem-boundary"

export const PRIMARY_ROUTE_MODULES = [
  "src/features/overview/overview-page.tsx",
  "src/features/members/members-page.tsx",
  "src/features/audit/audit-page.tsx",
  "src/features/settings/capabilities-page.tsx",
  "src/features/settings/business-rules-page.tsx",
]

/** Estimator screens, present only when the deployment's rollout switch is on (task 7.9). */
export const ESTIMATOR_ROUTE_MODULES = [
  "src/features/documents/intake-page.tsx",
  "src/features/workbench/requests-page.tsx",
  "src/features/workbench/estimator/estimator-page.tsx",
  "src/features/workbench/search-page.tsx",
  "src/features/print/print-page.tsx",
]

const estimatorRoutes: RouteObject[] = [
  { path: "documents", lazy: () => import("../features/documents/intake-page").then((m) => ({ Component: m.IntakePage })) },
  { path: "workbench", lazy: () => import("../features/workbench/requests-page").then((m) => ({ Component: m.RequestsPage })) },
  {
    path: "workbench/:requestId",
    lazy: () => import("../features/workbench/estimator/estimator-page").then((m) => ({ Component: m.EstimatorPage })),
  },
  { path: "search", lazy: () => import("../features/workbench/search-page").then((m) => ({ Component: m.SearchPage })) },
]

export function buildRoutes(estimator: boolean): RouteObject[] {
  return [
  {
    element: <Layout />,
    errorElement: <ProblemBoundary />,
    children: [
      { index: true, lazy: () => import("../features/overview/overview-page").then((m) => ({ Component: m.OverviewPage })) },
      { path: "members", lazy: () => import("../features/members/members-page").then((m) => ({ Component: m.MembersPage })) },
      { path: "audit", lazy: () => import("../features/audit/audit-page").then((m) => ({ Component: m.AuditPage })) },
      {
        path: "settings/capabilities",
        lazy: () => import("../features/settings/capabilities-page").then((m) => ({ Component: m.CapabilitiesPage })),
      },
      {
        path: "settings/business-rules",
        lazy: () => import("../features/settings/business-rules-page").then((m) => ({ Component: m.BusinessRulesPage })),
      },
      ...(estimator ? estimatorRoutes : []),
      { path: "*", element: <ProblemView error={{ code: "request.not_found" }} /> },
    ],
  },
  // The internal print summary renders without the application chrome.
  ...(estimator
    ? [
        {
          path: "print/quote-versions/:versionId",
          errorElement: <ProblemBoundary />,
          lazy: () => import("../features/print/print-page").then((m) => ({ Component: m.PrintPage })),
        },
      ]
    : []),
  // The sign-in callback is completed before the router exists; if it is ever routed, go home.
  { path: "auth/callback", element: <Navigate to="/" replace /> },
  ]
}

export const routes: RouteObject[] = buildRoutes(true)

export function createRouter() {
  return createBrowserRouter(buildRoutes(estimatorEnabled()))
}

/** Creates the router once signed in, so its initial location is the post-sign-in URL. */
export function AppRouter() {
  const [router] = useState(createRouter)
  return <RouterProvider router={router} />
}
